import { ParticleEffect } from './ParticleEffect';
import type { Particle, IntensityColorScheme } from '../types';

/**
 * Winter snow effect
 */
export class WinterEffect extends ParticleEffect {
    protected getDefaultVariant(): string {
        return 'light';
    }

    protected getDefaultColorScheme(): IntensityColorScheme {
        return {
            minimal: '#e0f2fe',  // Very light blue
            subtle: '#bae6fd',   // Light blue
            moderate: '#7dd3fc', // Medium blue
            bold: '#38bdf8',     // Bright blue
        };
    }

    init(width: number, height: number): void {
        const count = this.getParticleCount();
        this.particles = [];

        for (let i = 0; i < count; i++) {
            this.particles.push(this.createSnowflake(width, height, true));
        }
    }

    private createSnowflake(width: number, height: number, randomY = false): Particle {
        const baseSize = this.variant === 'blizzard' ? 4 : 2;
        const sizeVariance = this.variant === 'blizzard' ? 3 : 2;
        const sizeMultiplier = this.getSizeMultiplier();
        const speedMultiplier = this.getSpeedMultiplier();

        const particle: Particle = {
            x: Math.random() * width,
            y: randomY ? Math.random() * height : -10,
            vx: (Math.random() - 0.5) * 0.5 * speedMultiplier, // Slight horizontal drift
            vy: (0.5 + Math.random() * 1.5) * speedMultiplier, // Gentle falling
            size: (baseSize + Math.random() * sizeVariance) * sizeMultiplier,
            opacity: 0.3 + Math.random() * 0.7,
            rotation: Math.random() * Math.PI * 2,
            rotationSpeed: (Math.random() - 0.5) * 0.02,
            color: this.getIntensityColor(),
        };

        // Try to add custom image if available (may be assigned later if still loading)
        if (this.hasCustomImages()) {
            particle.image = this.getRandomImage();
        }

        return particle;
    }

    update(_deltaTime: number, width: number, height: number): void {
        // Ensure particles have images if they loaded after particle creation
        this.ensureParticleImages();

        for (let i = 0; i < this.particles.length; i++) {
            const p = this.particles[i];

            // Add sinusoidal movement
            const time = Date.now() / 1000;
            p.x += p.vx + Math.sin(time + i) * 0.2;
            p.y += p.vy;
            p.rotation += p.rotationSpeed;

            // Reset particle if it goes off screen
            if (p.y > height + 10) {
                this.particles[i] = this.createSnowflake(width, height, false);
            }

            // Wrap horizontally
            if (p.x < -10) p.x = width + 10;
            if (p.x > width + 10) p.x = -10;
        }
    }

    render(ctx: CanvasRenderingContext2D): void {
        for (const p of this.particles) {
            this.renderParticle(ctx, p);
        }
    }
}
